import { useState, useMemo } from "react";
import { useViewport } from "../hooks";
import { GlobalStyles } from "./GlobalStyles";
import { BrandBackground, NoiseOverlay } from "./Background";
import { Header, TopNav, Sidebar, MobileNav, type TabId } from "./Header";
import { Hero } from "./Hero";
import { FilterBar } from "./FilterBar";
import { SectionWrap, SectionHeader, Environments, Pipeline, FAQSection } from "./Sections";
import { PackageCard } from "./PackageCard";
import { PackageDrawer } from "./PackageDrawer";
import { MetadataView } from "./MetadataView";
import { CoverageView } from "./CoverageView";
import { OnboardingView } from "./OnboardingView";
import { Footer } from "./Footer";
import { StickyMobileBar } from "./StickyMobileBar";
import { COLORS } from "../styles/tokens";
import { PACKAGES, ttlBucketMatch } from "../data/packages";
import type { SignalPackage, OnboardingPayload, TtlBucket } from "../types/realsynq";

export interface RealSynqExplorerProps {
  onOnboardingSubmit?: (payload: OnboardingPayload) => Promise<void> | void;
}

export default function RealSynqExplorer({ onOnboardingSubmit }: RealSynqExplorerProps = {}) {
  const { isMobile } = useViewport();
  const [active, setActive] = useState<SignalPackage | null>(null);
  const [vertical, setVertical] = useState("All");
  const [path, setPath] = useState("All");
  const [ttl, setTtl] = useState("All");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabId>("explorer");
  const [navOpen, setNavOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const allVerticals = useMemo(() => {
    const s = new Set<string>();
    PACKAGES.forEach(p => p.verticals.forEach(v => s.add(v)));
    return ["All", ...Array.from(s).sort()];
  }, []);

  const filtered = useMemo(() => {
    return PACKAGES.filter(p => {
      if (vertical !== "All" && !(p.verticals as readonly string[]).includes(vertical)) return false;
      if (path !== "All" && !(p.activation as readonly string[]).includes(path)) return false;
      if (ttl !== "All" && !ttlBucketMatch(p.ttlRange, ttl as TtlBucket)) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = (p.name + p.tagline + p.signals.map(s => s.name + s.desc).join(" ")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [vertical, path, ttl, search]);

  const totalSignals = PACKAGES.reduce((s, p) => s + (p.signalCount ?? 0), 0);
  const activeFilterCount =
    [vertical, path, ttl].filter(v => v !== "All").length + (search ? 1 : 0);

  const clearFilters = () => {
    setVertical("All");
    setPath("All");
    setTtl("All");
    setSearch("");
  };

  const tabContent = (
    <>
      {tab === "explorer" && (
        <div className="layer">
          <Hero setTab={setTab} totalSignals={totalSignals} isMobile={isMobile} />

          <FilterBar
            search={search}
            setSearch={setSearch}
            vertical={vertical}
            setVertical={setVertical}
            path={path}
            setPath={setPath}
            ttl={ttl}
            setTtl={setTtl}
            allVerticals={allVerticals}
            filtered={filtered}
            clearFilters={clearFilters}
            activeFilterCount={activeFilterCount}
            isMobile={isMobile}
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
          />

          <SectionWrap markerNum="01" markerLabel="CATALOG">
            <SectionHeader
              eyebrow="◆ SIGNAL PACKAGES"
              title="Catalog"
              version="v2026.1"
              isMobile={isMobile}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))",
                gap: isMobile ? 12 : 16,
              }}
            >
              {filtered.map((p, i) => (
                <PackageCard key={p.id} pkg={p} onOpen={() => setActive(p)} index={i} />
              ))}
              {filtered.length === 0 && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: isMobile ? 40 : 60,
                    textAlign: "center",
                    color: COLORS.inkDim,
                    border: `1px dashed ${COLORS.border}`,
                    borderRadius: 8,
                  }}
                >
                  No packages match these filters.
                </div>
              )}
            </div>
          </SectionWrap>

          <Environments isMobile={isMobile} />
          <Pipeline isMobile={isMobile} />
          <FAQSection isMobile={isMobile} />
        </div>
      )}

      {tab === "metadata" && (
        <div className="layer">
          <MetadataView isMobile={isMobile} />
        </div>
      )}

      {tab === "coverage" && (
        <div className="layer">
          <CoverageView isMobile={isMobile} />
        </div>
      )}

      {tab === "onboard" && (
        <div className="layer">
          <OnboardingView
            onBack={() => setTab("explorer")}
            isMobile={isMobile}
            onSubmit={onOnboardingSubmit}
          />
        </div>
      )}

      <Footer onNavigateToOnboard={() => setTab("onboard")} />
    </>
  );

  if (isMobile) {
    return (
      <div
        style={{
          background: COLORS.bg,
          color: COLORS.ink,
          minHeight: "100vh",
          fontFamily: "'DM Sans', system-ui, sans-serif",
          position: "relative",
        }}
      >
        <GlobalStyles />
        <BrandBackground />
        <NoiseOverlay />
        <Header
          tab={tab}
          setTab={setTab}
          totalSignals={totalSignals}
          navOpen={navOpen}
          setNavOpen={setNavOpen}
          isMobile={isMobile}
        />
        {navOpen && (
          <MobileNav
            tab={tab}
            setTab={(t) => { setTab(t); setNavOpen(false); }}
            onClose={() => setNavOpen(false)}
          />
        )}
        {tabContent}
        {tab === "explorer" && !active && (
          <StickyMobileBar
            activeFilterCount={activeFilterCount}
            setFiltersOpen={setFiltersOpen}
            setTab={setTab}
          />
        )}
        {active && <PackageDrawer pkg={active} onClose={() => setActive(null)} isMobile={isMobile} onRequestPackage={() => { setActive(null); setTab("onboard"); }} />}
      </div>
    );
  }

  // Desktop: flex-column root keeps TopNav always visible;
  // flex-row body lets the sidebar stay fixed while main scrolls.
  return (
    <div
      style={{
        background: COLORS.bg,
        color: COLORS.ink,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <GlobalStyles />
      <BrandBackground />
      <NoiseOverlay />

      <TopNav totalSignals={totalSignals} />

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Sidebar tab={tab} setTab={setTab} totalSignals={totalSignals} />
        <main style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {tabContent}
        </main>
      </div>

      {active && <PackageDrawer pkg={active} onClose={() => setActive(null)} isMobile={isMobile} onRequestPackage={() => { setActive(null); setTab("onboard"); }} />}
    </div>
  );
}
